import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPhone, FaCommentDots } from 'react-icons/fa';

const scanLine = keyframes`
  0% { transform: translateY(-100%); }
  50% { transform: translateY(100%); }
  100% { transform: translateY(-100%); }
`;

const slideInFromTop = keyframes`
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const floatingParticles = keyframes`
  0% { transform: translateY(0) translateX(0) rotate(0); }
  25% { transform: translateY(-10px) translateX(5px) rotate(5deg); }
  50% { transform: translateY(0) translateX(10px) rotate(0); }
  75% { transform: translateY(10px) translateX(5px) rotate(-5deg); }
  100% { transform: translateY(0) translateX(0) rotate(0); }
`;

const dataFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 100%; }
`;

const textGlowPulse = keyframes`
  0% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
  50% { text-shadow: 0 0 20px rgba(100, 182, 255, 0.8), 0 0 30px rgba(100, 182, 255, 0.6), 0 0 40px rgba(100, 182, 255, 0.4); }
  100% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
`;

const leadValueEntry = keyframes`
  0% { opacity: 0; transform: translateY(20px) scale(0.8); clip-path: inset(0 50% 0 50%); filter: blur(10px); }
  30% { opacity: 0.5; transform: translateY(10px) scale(0.9); clip-path: inset(0 30% 0 30%); filter: blur(5px); }
  60% { opacity: 0.8; transform: translateY(0) scale(1); clip-path: inset(0 10% 0 10%); filter: blur(2px); }
  75% { clip-path: inset(0 0 0 0); filter: blur(0); }
  85% { transform: translateY(0) scale(1.05); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const leadValueHighlight = keyframes`
  0% { text-shadow: 0 0 10px rgba(0, 255, 204, 0.6); }
  50% { text-shadow: 0 0 25px rgba(0, 255, 204, 0.9), 0 0 40px rgba(0, 255, 204, 0.4); }
  100% { text-shadow: 0 0 10px rgba(0, 255, 204, 0.6); }
`;

const leadBorderPulse = keyframes`
  0% { box-shadow: 0 0 0 1px rgba(0, 255, 204, 0.3); }
  50% { box-shadow: 0 0 0 2px rgba(0, 255, 204, 0.6), 0 0 20px rgba(0, 255, 204, 0.4); }
  100% { box-shadow: 0 0 0 1px rgba(0, 255, 204, 0.3); }
`;

const revealFadeIn = keyframes`
  0% { opacity: 0; transform: translateY(24px) scale(0.96); filter: blur(6px); }
  60% { opacity: 0.7; filter: blur(2px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  cursor: ${props => props.hasMoreSteps ? 'pointer' : 'default'};
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(10, 20, 40, 0.7)),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 182, 255, 0.05) 2px,
        rgba(100, 182, 255, 0.05) 4px
      );
    z-index: -1;
    opacity: 0.8;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(100, 182, 255, 0.05) 50%,
      transparent 100%
    );
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }
`;

const ContentPreview = styled.div`
  max-width: 80%;
  width: 80%;
  margin: 0 auto;
  padding: 30px 40px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.2);
  backdrop-filter: blur(10px);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }

  opacity: 0;
  animation: ${scaleIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.2s;
`;

const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: ${props => props.opacity || 0.5};
  animation: ${floatingParticles} ${props => props.duration || '8s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
`;

const TitleHighlight = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #64B6FF;
  margin-bottom: 24px;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.2s;
`;

const CheckoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
`;

const LeadCostHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.2rem;
  color: #00FFCC;
  text-shadow: 0 0 15px rgba(0, 255, 204, 0.6);
  margin-top: 30px;
  margin-bottom: 10px;
  padding: 12px 25px;
  border-radius: 8px;
  background: rgba(0, 255, 204, 0.05);
  border: 1px solid rgba(0, 255, 204, 0.2);
  position: relative;
  animation:
    ${leadValueEntry} 1.2s cubic-bezier(0.23, 1, 0.32, 1) forwards,
    ${leadValueHighlight} 3s infinite 1.2s,
    ${textGlowPulse} 4s infinite 2s;
  opacity: 0;
  transform-origin: center;

  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 1px solid rgba(0, 255, 204, 0.3);
    border-radius: 10px;
    animation: ${leadBorderPulse} 2.5s infinite;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(45deg, transparent 65%, rgba(0, 255, 204, 0.1) 75%, transparent 85%);
    background-size: 200% 200%;
    border-radius: 8px;
    animation: ${dataFlow} 3s linear infinite;
    z-index: -1;
  }
`;

const SubLabel = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  color: #64B6FF;
  opacity: 0.85;
  text-align: center;
  margin-top: 8px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  font-size: 2rem;
  color: #00FFCC;
  opacity: 0;
  animation: ${revealFadeIn} 0.7s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  animation-delay: 0.5s;
`;

const AnimatedReveal = styled.div`
  animation: ${revealFadeIn} 0.7s cubic-bezier(0.23, 1, 0.32, 1);
`;

const particles = [
  { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '15%', left: '10%', duration: '9s', delay: '0s', opacity: 0.7 },
  { size: '3px', color: 'rgba(0, 255, 204, 0.6)', top: '25%', left: '20%', duration: '10s', delay: '0.5s', opacity: 0.6 },
  { size: '6px', color: 'rgba(100, 182, 255, 0.5)', top: '60%', left: '85%', duration: '12s', delay: '1s', opacity: 0.5 },
  { size: '4px', color: 'rgba(0, 255, 204, 0.7)', top: '80%', left: '30%', duration: '11s', delay: '1.5s', opacity: 0.7 },
  { size: '7px', color: 'rgba(100, 182, 255, 0.6)', top: '40%', left: '70%', duration: '9s', delay: '2s', opacity: 0.6 },
  { size: '3px', color: 'rgba(0, 255, 255, 0.8)', top: '20%', left: '80%', duration: '8s', delay: '2.5s', opacity: 0.8 },
  { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '70%', left: '10%', duration: '10s', delay: '3s', opacity: 0.7 },
  { size: '4px', color: 'rgba(0, 255, 204, 0.6)', top: '35%', left: '40%', duration: '11s', delay: '3.5s', opacity: 0.6 },
];

const RivieraSlideNutricao = () => {
  const [revealed, setRevealed] = useState(false);

  const handleSlideClick = () => {
    if (!revealed) {
      setRevealed(true);
    }
  };

  return (
    <SlideWrapper onClick={handleSlideClick} hasMoreSteps={!revealed}>
      <ParticleContainer>
        {particles.map((p, i) => (
          <Particle key={i} {...p} />
        ))}
      </ParticleContainer>
      <ContentPreview>
        <CheckoutContainer>
          <TitleHighlight>Nutrição</TitleHighlight>
          {revealed && (
            <AnimatedReveal>
              <LeadCostHighlight>R$ 2,50 por lead em média</LeadCostHighlight>
              <SubLabel>para enviar mensagens e ligações</SubLabel>
              <IconRow>
                <FaCommentDots />
                <FaPhone />
              </IconRow>
            </AnimatedReveal>
          )}
        </CheckoutContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default RivieraSlideNutricao;
